import { createPortal } from 'react-dom';
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  GlobalStyles,
  useMediaQuery,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { safeSupportLink, supportAnswerSources } from 'utils/supportAnswerSources';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import { FeatureFlagCode } from '__generated__/graphql';
import { useAuthContext } from 'context/auth.context';
import { useFeatureFlagContext } from 'context/featureFlag.context';
import { useSupportChatSession } from 'hooks/ai/useSupportChatSession';
import { useSupportSidebarOpen, useSupportLauncherSlot } from 'hooks/ai/useSupportSidebarOpen';
import { useSupportViewport } from 'hooks/ai/useSupportViewport';
import { SIDEBAR_RAIL_WIDTH } from 'components/Sidebar/layout';
import { supportChatKey } from 'utils/supportChatStorage';

function AssistantSession({ storageKey }: { storageKey: string }) {
  const session = useSupportChatSession(storageKey);
  const [open, setOpen] = useState(false);
  const mobile = useMediaQuery(
    '(max-width: 599.95px), (pointer: coarse) and (orientation: landscape) and (max-height: 500px)'
  );
  const sidebarOpen = useSupportSidebarOpen();
  const narrow = useMediaQuery('(max-width: 599.95px)');
  const toolbarSlot = useSupportLauncherSlot();
  const launcherSlot = narrow ? toolbarSlot : null;
  const viewport = useSupportViewport(open && mobile && !sidebarOpen);
  const launcher = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const transcript = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open && !mobile) input.current?.focus();
  }, [open, session.canChat, mobile]);
  useEffect(() => {
    if (open && transcript.current) transcript.current.scrollTop = transcript.current.scrollHeight;
  }, [open, session.chat.messages.length, session.sending]);
  const minimize = () => {
    setOpen(false);
    launcher.current?.focus();
  };
  const panel = (
    <Paper
      component='section'
      aria-label='ReStore Assistant'
      onKeyDown={(event) => {
        if (event.key === 'Escape') minimize();
      }}
      elevation={6}
      sx={{
        width: mobile ? '100%' : 400,
        maxWidth: '100%',
        height: mobile ? '100%' : 'min(600px, calc(100dvh - 176px))',
        minHeight: 0,
        boxSizing: 'border-box',
        ...(mobile && {
          padding:
            'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
          '& button': { minHeight: 44, minWidth: 44 },
          '& textarea': { fontSize: '16px' },
        }),
        display: 'flex',
        flexDirection: 'column',
        mb: mobile ? 0 : 2,
        borderRadius: mobile ? 0 : 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flexShrink: 0, p: mobile ? 1.5 : 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='subtitle2'>ReStore Assistant</Typography>
            <Typography variant='caption'>AI help for ReStore</Typography>
          </Box>
          <IconButton aria-label='Minimize assistant' onClick={minimize} sx={{ flexShrink: 0 }}>
            <RemoveIcon />
          </IconButton>
        </Box>
        {session.canChat && (
          <Button
            size='small'
            variant='outlined'
            sx={{ mt: 1 }}
            onClick={() => {
              session.reset();
              if (!mobile) input.current?.focus();
            }}
          >
            Start a new chat
          </Button>
        )}
      </Box>
      <Box ref={transcript} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        {session.policyLoading ? (
          <Box role='status'>
            <CircularProgress size={20} /> Loading AI policy…
          </Box>
        ) : session.policyError || !session.currentVersion ? (
          <Alert
            severity='error'
            action={
              <Button
                onClick={() => {
                  void session.retryPolicy().catch(() => {});
                }}
              >
                Retry
              </Button>
            }
          >
            Unable to load the AI policy.
          </Alert>
        ) : session.needsAcceptance ? (
          <Box>
            <Typography variant='subtitle1'>Before we start</Typography>
            <Typography sx={{ mb: 2 }}>
              Read and accept the current AI policy to use the assistant.
            </Typography>
            <Box sx={{ mb: 2, overflowWrap: 'anywhere' }}>
              <ReactMarkdown>{session.policyText}</ReactMarkdown>
            </Box>
            {session.acceptError && <Alert severity='error'>{session.acceptError}</Alert>}
            <Button
              variant='contained'
              disabled={session.accepting}
              onClick={() => {
                void session.acceptPolicy().catch(() => {});
              }}
            >
              {session.accepting ? 'Accepting…' : 'Accept and continue'}
            </Button>
          </Box>
        ) : (
          <>
            {!session.chat.messages.length && (
              <Box sx={{ py: 2 }}>
                <Typography variant='subtitle1'>How can I help?</Typography>
                <Typography sx={{ mt: 1 }}>
                  Ask about using ReStore. I can use the page you’re viewing to help answer your
                  question.
                </Typography>
              </Box>
            )}
            <Box
              role='log'
              aria-label='Conversation'
              aria-live='polite'
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              {session.chat.messages.map((message, index) => {
                const { answer, sources } = supportAnswerSources(message.text, message.citations);
                return (
                  <Box
                    key={index}
                    sx={{
                      alignSelf: message.role === 'user' ? 'flex-end' : 'stretch',
                      maxWidth: message.role === 'user' ? '85%' : '100%',
                      p: 1.5,
                      borderRadius: message.role === 'user' ? '12px 0 12px 12px' : 0,
                      backgroundColor: (theme) =>
                        message.role === 'user' ? theme.colors.ourCommentLight : 'transparent',
                      overflowWrap: 'anywhere',
                      '& p': { mt: 0, mb: 1 },
                      '& pre': { whiteSpace: 'pre-wrap' },
                    }}
                  >
                    <Typography variant='caption' color='text.secondary'>
                      {message.role === 'user' ? 'You' : 'ReStore assistant'}
                    </Typography>
                    {message.role === 'user' ? (
                      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{message.text}</Typography>
                    ) : (
                      <ReactMarkdown
                        components={{
                          a: ({ href, children }) =>
                            href && safeSupportLink(href) ? (
                              <Link href={href} target='_blank' rel='noopener noreferrer'>
                                {children}
                              </Link>
                            ) : (
                              <span>{children}</span>
                            ),
                          img: () => null,
                        }}
                      >
                        {answer}
                      </ReactMarkdown>
                    )}
                    {message.role === 'assistant' && sources.length > 0 && (
                      <Box
                        component='nav'
                        aria-label='Answer sources'
                        sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}
                      >
                        {sources.map((citation) => (
                          <Link
                            key={citation.url}
                            href={citation.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              minHeight: 44,
                              py: 0.5,
                              color: 'primary.main',
                              textUnderlineOffset: '3px',
                              '&:focus-visible': {
                                outline: '2px solid',
                                outlineColor: 'primary.main',
                                outlineOffset: 2,
                                borderRadius: 1,
                              },
                            }}
                          >
                            <MenuBookOutlinedIcon
                              aria-hidden='true'
                              sx={{ fontSize: 18, flexShrink: 0 }}
                            />
                            <Box component='span' sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                              {citation.title || citation.heading || 'Documentation'}
                            </Box>
                            <NorthEastIcon
                              aria-hidden='true'
                              sx={{ fontSize: 16, flexShrink: 0 }}
                            />
                          </Link>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            {session.sending && (
              <Box role='status' sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ flexShrink: 0 }} />
                <Typography>Finding an answer…</Typography>
              </Box>
            )}
            {session.error && (
              <Alert
                severity='error'
                sx={{ mt: 2 }}
                action={
                  <Button
                    disabled={session.sending}
                    onClick={() => {
                      void session.send(true);
                    }}
                  >
                    Retry
                  </Button>
                }
              >
                {session.error}
              </Alert>
            )}
          </>
        )}
      </Box>
      {session.canChat && (
        <Box
          component='form'
          onSubmit={(event) => {
            event.preventDefault();
            void session.send();
          }}
          sx={{ flexShrink: 0, p: mobile ? 1.5 : 2, borderTop: 1, borderColor: 'divider' }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: mobile ? 'center' : 'flex-end' }}>
            <TextField
              inputRef={input}
              multiline
              minRows={mobile ? 1 : 2}
              maxRows={mobile ? 3 : 5}
              fullWidth
              placeholder='Ask a question…'
              value={session.chat.draft}
              onChange={(event) => session.setDraft(event.target.value)}
              inputProps={{ maxLength: 2000, 'aria-label': 'Your question' }}
              onKeyDown={(event) => {
                if (
                  !mobile &&
                  event.key === 'Enter' &&
                  !event.shiftKey &&
                  !event.nativeEvent.isComposing
                ) {
                  event.preventDefault();
                  void session.send();
                }
              }}
            />
            <IconButton
              type='submit'
              color='primary'
              aria-label='Send message'
              disabled={session.sending || !session.chat.draft.trim()}
            >
              <SendIcon />
            </IconButton>
          </Box>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
            {session.chat.draft.length}/2000 · AI answers may be inaccurate. Check the sources.
          </Typography>
        </Box>
      )}
    </Paper>
  );
  const launcherButton = (
    <Box
      sx={{
        display: mobile && (open || sidebarOpen) ? 'none' : 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <Button
        data-support-launcher
        ref={launcher}
        variant='contained'
        aria-expanded={open}
        startIcon={<ChatBubbleOutlineIcon />}
        onClick={() => setOpen((value) => !value)}
        sx={{ borderRadius: 8, minHeight: 48 }}
      >
        Ask ReStore
      </Button>
    </Box>
  );
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 'calc(16px + env(safe-area-inset-bottom))',
        right: 16,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        // Reserve the rail's space on desktop and the bottom toolbar's space on mobile.
        'body:has([data-sidebar-rail]) &': {
          right: { xs: 16, sm: `calc(${SIDEBAR_RAIL_WIDTH} + 16px)` },
          bottom: { xs: 'calc(5rem + 16px + env(safe-area-inset-bottom))', sm: 16 },
          maxWidth: { xs: 'calc(100vw - 32px)', sm: `calc(100vw - ${SIDEBAR_RAIL_WIDTH} - 32px)` },
        },
        // Keep the session mounted while a sidebar owns the interaction area.
        'body:has([data-sidebar-drawer-open="true"]) &': { display: 'none' },
        maxWidth: 'calc(100vw - 32px)',
        ...(mobile && {
          left: 0,
          right: 0,
          bottom: 0,
          maxWidth: 'none',
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          p: 1,
          pb: 'calc(8px + env(safe-area-inset-bottom))',
          'body:has([data-sidebar-rail]) &': {
            right: { xs: 0, sm: SIDEBAR_RAIL_WIDTH },
            bottom: { xs: '5rem', sm: 0 },
            maxWidth: 'none',
          },
        }),
        ...(launcherSlot && { display: 'contents' }),
      }}
    >
      {mobile && !launcherSlot && (
        <GlobalStyles
          styles={{
            '#contentContainer': {
              paddingBottom: 'calc(64px + env(safe-area-inset-bottom))',
              scrollPaddingBottom: 'calc(64px + env(safe-area-inset-bottom))',
            },
          }}
        />
      )}
      {mobile ? (
        <Dialog
          open={open && !sidebarOpen}
          fullScreen
          TransitionProps={{ onExited: () => launcher.current?.focus() }}
          onClose={minimize}
          aria-label='ReStore Assistant'
          PaperProps={{ sx: { m: 0, width: '100%', height: '100%', maxHeight: 'none' } }}
          sx={{
            // Dialog form defaults assume stacked labels; the chat composer has none.
            '& .MuiFormControl-root': { mt: 0, mb: 0, minWidth: 0 },
            '& .MuiInputBase-multiline': { mt: 0 },
            top: viewport?.top ?? 0,
            left: viewport?.left ?? 0,
            right: 'auto',
            bottom: 'auto',
            width: viewport?.width ?? '100%',
            height: viewport?.height ?? '100dvh',
          }}
        >
          {panel}
        </Dialog>
      ) : open ? (
        panel
      ) : null}
      {launcherSlot ? createPortal(launcherButton, launcherSlot) : launcherButton}
    </Box>
  );
}

export default function SupportAssistant() {
  const { user, userTenantId, loading } = useAuthContext();
  const flags = useFeatureFlagContext();
  const userId = user?.user.id;
  if (
    loading ||
    !userId ||
    !userTenantId ||
    flags.featureFlagsLoading ||
    flags.featureFlagsError ||
    !flags.checkFeatureFlagIsEnabled(FeatureFlagCode.InAppSupportAssistant)
  )
    return null;
  const key = supportChatKey(userId, userTenantId);
  return <AssistantSession key={key} storageKey={key} />;
}
